import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Typography, Box, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import api from '../api';
import { InputSection } from '../components/common/InputSection';
import PageTitle from '../components/common/PageTitle';
import { betColumns, refNoCol } from '../components/ReportTableColumns';
import { SearchWrapper } from '../components/SearchWrapper';
import { Table } from '../components/Table';
import LinkCol from '../components/LinkCol';
import PopupEnum from '../enum/popupEnum';
import { useError } from '../hook/useError';
import { BetData, GetBetDetailByRefNoRequest } from '../models';
import { usePopupStore } from '../store/popupStore';
import { Option } from '../components/common/SearchSelect';
import { GameSection } from '../components/GameSection';
import SectionTitle from '../components/common/SectionTitle';

export const BetQueryPage = () => {
	const { t, i18n } = useTranslation();
	const [data, setData] = useState<BetData | null>(null);
	const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
	const [game, setGame] = useState<Option | null>(null);
	const [gameRefNo, setGameRefNo] = useState<string>('');
	const [refNo, setRefNo] = useState<string>('');
	const { openPopup, closePopup } = usePopupStore();
	const { handleError } = useError();

	async function handleSearch() {
		try {
			if (refNo === '') throw PopupEnum.InvalidInput;
			
			openPopup(PopupEnum.Loading);

			const res = await api.getBetByRefNo({
				refNo,
			});
			
			if (!res.data) throw PopupEnum.ResultNotFound;
			setData(res.data);
			closePopup();
		} catch (error: any | PopupEnum) {
			handleError(error);
			setData(null);
		}
	}

	async function handleGameSearch() {
		try {
			if (gameRefNo === '' || !game) throw PopupEnum.InvalidInput;
			
			openPopup(PopupEnum.Loading);

			const res = await api.getBetByGameRefNo({
				refNo: gameRefNo,
				gameCode: game.id,
			});
			
			if (!res.data) throw PopupEnum.ResultNotFound;
			setData(res.data);
			closePopup();
		} catch (error: any | PopupEnum) {
			handleError(error);
			setData(null);
		}
	}

	const getBetDetailByRefNo = async (refNo: string) => {
		try {
			if (!data) return;
			if (!data.fpId || !data.playerId) {
				handleError(new Error('Missing required information (fpId or playerId)'));
				return;
			}

			const req: GetBetDetailByRefNoRequest = {
				refNo,
				gameCode: data.gameCode,
				fpId: data.fpId,
				playerId: data.playerId,
				lang: i18n.language,
			};

			const { url } = await api.getBetDetailByRefNo(req);
			if (!url) throw PopupEnum.ResultNotFound;

			window.open(url, '_blank');
		} catch (error: any) {
			console.log(error);
			handleError(error);
		}
	};

	return (
		<div className="space-y-4">
			<PageTitle content={t('sidebar.report.singleBetQuery')} />
			<SearchWrapper handleSearch={() => handleSearch()} showCurrencyHint>
				<InputSection
					title={t('reportTable.RefNo')}
					label={t('search.Input_RefNo')}
					value={refNo}
					setValue={setRefNo}
					widthStyle="w-[300px]"
				/>
			</SearchWrapper>
			
			<div className="mt-4">
				<Typography
					variant="body2"
					className="text-blue-600 cursor-pointer hover:text-blue-800 inline-flex items-center gap-2"
					onClick={() => setShowAdvanced(!showAdvanced)}
				>
					<span>{t('search.NeedToSearchByGameRefNo') || 'Need to search by game reference number?'}</span>
					<span className="text-gray-600">(Advanced</span>
					{showAdvanced ? <ExpandLess fontSize="small" className="text-gray-600" /> : <ExpandMore fontSize="small" className="text-gray-600" />}
					<span className="text-gray-600">)</span>
				</Typography>
				
				<Collapse in={showAdvanced}>
					<Box className="mt-4 space-y-4">
						<SectionTitle content={t('search.SearchByGameReferenceNumber') || 'Search by Game Reference Number'} />
						
						<Box className="p-3 bg-gray-50 rounded">
							<Typography variant="body2" className="text-gray-700 whitespace-pre-line">
								{t('search.ReferenceNumberGameDesc') || 'The original reference number shown to the player in the game, without prefix.\nExample: 100123456789'}
							</Typography>
						</Box>
						
						<SearchWrapper handleSearch={() => handleGameSearch()} showCurrencyHint>
							<GameSection game={game} setGame={setGame} />
							<InputSection
								title={t('reportTable.RefNo')}
								label={t('search.Input_RefNo')}
								value={gameRefNo}
								setValue={setGameRefNo}
								widthStyle="w-[300px]"
							/>
						</SearchWrapper>
					</Box>
				</Collapse>
			</div>
			
			{data && (
				<div className="space-y-12">
					<Table 
						list={[data]} 
						columns={betColumns.map((col) => {
							if (col === refNoCol) {
								return (t: any) => ({
									...refNoCol(t),
									renderCell: (params: any) => <LinkCol value={params.value} click={getBetDetailByRefNo} />,
								});
							}
							return col;
						})} 
					/>
				</div>
			)}
		</div>
	);
};

export default BetQueryPage;
